import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCan, useOrganizationId, useSubjectLanguageVariants } from '@features/user';
import { Alert, Box, Button, Container, Stack, Typography } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';

import { MissingRequirementListView } from './components/MissingRequirementListView';
import { useMissingRequirements } from './store/useMissingRequirements';

/**
 * The missing-card list (GDN-3226, AC-2): patients whose payment-method
 * requirement is open, straight from the reconciled backend list.
 *
 * This is its own screen rather than a filter on the patients list, which the
 * product direction keeps unchanged for the MVP. The bulk Requirements center
 * (AC-10 … AC-16) is a separate, currently held surface.
 */
export const MissingRequirementListPage = () => {
  const subject = useSubjectLanguageVariants();
  const enabled = useFeatureGate(FeatureGates.PATIENT_REQUIREMENTS);
  const canManageBilling = useCan('manage_billing');
  const navigate = useNavigate();
  const { patientRequirementsWorklist } = useEventTracking();
  const organizationId = useOrganizationId();
  const [page, setPage] = useState(1);

  const {
    rows,
    total,
    perPage,
    isLoading,
    isError,
    requirementLabel,
    definitionMissing,
    refetch,
  } = useMissingRequirements('payment_method', page, undefined, enabled && canManageBilling);

  // One opened event per mount, after the first load resolves — before it, the
  // total is 0 for a worklist that may turn out to be full.
  const openedTracked = useRef(false);
  useEffect(() => {
    if (openedTracked.current || isLoading || isError || !enabled || !canManageBilling) {
      return;
    }
    openedTracked.current = true;
    patientRequirementsWorklist.opened({
      organization_id: organizationId ?? undefined,
      kind: 'payment_method',
      total,
    });
  }, [
    isLoading,
    isError,
    enabled,
    canManageBilling,
    total,
    organizationId,
    patientRequirementsWorklist,
  ]);

  const handlePageChange = (nextPage: number) => {
    patientRequirementsWorklist.pageChanged({
      organization_id: organizationId ?? undefined,
      kind: 'payment_method',
      page: nextPage,
      direction: nextPage > page ? 'next' : 'previous',
    });
    setPage(nextPage);
  };

  const handleRetry = () => {
    patientRequirementsWorklist.retryClicked({
      organization_id: organizationId ?? undefined,
      kind: 'payment_method',
    });
    refetch();
  };

  const handlePatientClick = (patientId: string, rowIndex: number) => {
    patientRequirementsWorklist.patientClicked({
      organization_id: organizationId ?? undefined,
      patient_id: patientId,
      kind: 'payment_method',
      row_index: rowIndex,
    });
  };

  // Rollback semantics: with the gate off the staff surfaces disappear rather
  // than erroring, and the stored rows stay valid behind it.
  if (!enabled) return null;

  // The nav entry hides this from anyone without manage_billing, but the route
  // is reachable by URL, so the page has to enforce it too. Distinct from the
  // gate: a gate-off org sees nothing at all, while someone in a launched org
  // without the permission is told why rather than shown a blank page.
  if (!canManageBilling) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/patients')}>
              Back
            </Button>
          }
        >
          You do not have access to {subject.singular} billing for this practice.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {subject.pluralCap} missing a payment method
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A {subject.singular} with a saved card never appears here, even when a stale
            cache says otherwise.
          </Typography>
        </Box>

        {definitionMissing ? (
          <Alert severity="info">
            This practice has no payment-method requirement yet. Add one in Practice
            Settings to start tracking who still owes a card.
          </Alert>
        ) : (
          <MissingRequirementListView
            rows={rows}
            total={total}
            page={page}
            perPage={perPage}
            isLoading={isLoading}
            isError={isError}
            requirementLabel={requirementLabel || 'Payment method on file'}
            subject={subject}
            onPageChange={handlePageChange}
            onRetry={handleRetry}
            onPatientClick={handlePatientClick}
          />
        )}
      </Stack>
    </Container>
  );
};

export default MissingRequirementListPage;
